import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const UNSEEN_IDS_STORAGE_KEY_PREFIX = 'scheduler_unseen_reminders';
const SEEN_AT_CLOSE_STORAGE_KEY_PREFIX = 'scheduler_seen_at_close';
const SEEN_RESCHEDULE_AT_CLOSE_STORAGE_KEY_PREFIX = 'scheduler_seen_reschedule_at_close';

function getStoredUnseenIds(userId: string): Set<string> {
  try {
    const s = localStorage.getItem(`${UNSEEN_IDS_STORAGE_KEY_PREFIX}_${userId}`);
    if (s === null || s === '') return new Set();
    const arr = JSON.parse(s);
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function setStoredUnseenIds(userId: string, ids: Set<string>): void {
  try {
    localStorage.setItem(`${UNSEEN_IDS_STORAGE_KEY_PREFIX}_${userId}`, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

function getStoredSeenAtClose(userId: string): Set<string> {
  try {
    const s = localStorage.getItem(`${SEEN_AT_CLOSE_STORAGE_KEY_PREFIX}_${userId}`);
    if (s === null || s === '') return new Set();
    const arr = JSON.parse(s);
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function setStoredSeenAtClose(userId: string, ids: Set<string>): void {
  try {
    localStorage.setItem(`${SEEN_AT_CLOSE_STORAGE_KEY_PREFIX}_${userId}`, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

function getStoredSeenRescheduleAtClose(userId: string): Set<string> {
  try {
    const s = localStorage.getItem(`${SEEN_RESCHEDULE_AT_CLOSE_STORAGE_KEY_PREFIX}_${userId}`);
    if (s === null || s === '') return new Set();
    const arr = JSON.parse(s);
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function setStoredSeenRescheduleAtClose(userId: string, ids: Set<string>): void {
  try {
    localStorage.setItem(`${SEEN_RESCHEDULE_AT_CLOSE_STORAGE_KEY_PREFIX}_${userId}`, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

export type ReminderNotificationItem = {
  id: string;
  title?: string;
  start_time?: string;
  end_time?: string;
  project_id?: string;
  project_name?: string;
  participant_first_name?: string;
  participant_last_name?: string;
  moderator_name?: string;
};

type ReminderNotificationsContextValue = {
  unseenIds: Set<string>;
  setUnseenIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  lastReminders: ReminderNotificationItem[];
  setLastReminders: React.Dispatch<React.SetStateAction<ReminderNotificationItem[]>>;
  lastRescheduleReminders: ReminderNotificationItem[];
  setLastRescheduleReminders: React.Dispatch<React.SetStateAction<ReminderNotificationItem[]>>;
  /** IDs that were visible when the user last closed the popover (persisted so after refresh we don't show them as unseen again). */
  seenIdsAtClose: Set<string>;
  setSeenIdsAtClose: React.Dispatch<React.SetStateAction<Set<string>>>;
  /** Reschedule IDs when user last closed the popover; used so we don't treat 24h→reschedule as "already seen". */
  seenRescheduleIdsAtClose: Set<string>;
  setSeenRescheduleIdsAtClose: React.Dispatch<React.SetStateAction<Set<string>>>;
};

const ReminderNotificationsContext = createContext<ReminderNotificationsContextValue | null>(null);

const emptySet = new Set<string>();

const emptyContext: ReminderNotificationsContextValue = {
  unseenIds: emptySet,
  setUnseenIds: () => {},
  lastReminders: [],
  setLastReminders: () => {},
  lastRescheduleReminders: [],
  setLastRescheduleReminders: () => {},
  seenIdsAtClose: emptySet,
  setSeenIdsAtClose: () => {},
  seenRescheduleIdsAtClose: emptySet,
  setSeenRescheduleIdsAtClose: () => {},
};

export function ReminderNotificationsProvider({
  children,
  userId,
}: Readonly<{
  children: React.ReactNode;
  userId: string | null | undefined;
}>) {
  const [unseenIds, setUnseenIds] = useState<Set<string>>(() => new Set());
  const [lastReminders, setLastReminders] = useState<ReminderNotificationItem[]>([]);
  const [lastRescheduleReminders, setLastRescheduleReminders] = useState<ReminderNotificationItem[]>([]);
  const [seenIdsAtClose, setSeenIdsAtClose] = useState<Set<string>>(() => new Set());
  const [seenRescheduleIdsAtClose, setSeenRescheduleIdsAtClose] = useState<Set<string>>(() => new Set());
  const loadedUserIdRef = useRef<string | null>(null);

  // Restore unseen IDs and seen-at-close from localStorage when user becomes available (e.g. after login)
  useEffect(() => {
    if (userId && userId !== loadedUserIdRef.current) {
      loadedUserIdRef.current = userId;
      setUnseenIds(getStoredUnseenIds(userId));
      setSeenIdsAtClose(getStoredSeenAtClose(userId));
      setSeenRescheduleIdsAtClose(getStoredSeenRescheduleAtClose(userId));
    } else if (!userId) {
      loadedUserIdRef.current = null;
    }
  }, [userId]);

  // Persist unseen IDs whenever they change so state survives refresh
  useEffect(() => {
    if (userId != null) {
      setStoredUnseenIds(userId, unseenIds);
    }
  }, [userId, unseenIds]);

  // Persist seen-at-close whenever it changes so after refresh we don't re-show those as unseen
  useEffect(() => {
    if (userId != null) {
      setStoredSeenAtClose(userId, seenIdsAtClose);
    }
  }, [userId, seenIdsAtClose]);

  // Persist seen reschedule IDs at close so we don't treat 24h→reschedule as already seen
  useEffect(() => {
    if (userId != null) {
      setStoredSeenRescheduleAtClose(userId, seenRescheduleIdsAtClose);
    }
  }, [userId, seenRescheduleIdsAtClose]);

  const value: ReminderNotificationsContextValue = {
    unseenIds,
    setUnseenIds,
    lastReminders,
    setLastReminders,
    lastRescheduleReminders,
    setLastRescheduleReminders,
    seenIdsAtClose,
    setSeenIdsAtClose,
    seenRescheduleIdsAtClose,
    setSeenRescheduleIdsAtClose,
  };

  return (
    <ReminderNotificationsContext.Provider value={value}>
      {children}
    </ReminderNotificationsContext.Provider>
  );
}

export function useReminderNotifications(): ReminderNotificationsContextValue {
  return useContext(ReminderNotificationsContext) ?? emptyContext;
}
